import { useMemo, useState } from "react";
import { AlertTriangle, Loader2, PackageX } from "lucide-react";

import { businessAssetLossService } from "../../services/businessAssetLossService";
import { createClientRequestId } from "../../../utils/clientRequestId";

/**
 * Writing off a parcel that is not coming back.
 *
 * Used from both places the decision can arise: a failed delivery escalated to
 * management review, and a parcel lost while in a Rider's custody before any
 * attempt. One component so the wording, the validation and the refusals cannot
 * drift apart between them.
 *
 * It deliberately offers no receiver field and no rider field. The server
 * rejects both - the former Rider is read from locked custody - and showing them
 * would misrepresent what the command does.
 */

const REASONS = [
  { code: "MISSING", label: "Missing", hint: "Cannot be located, no evidence of theft or damage." },
  { code: "STOLEN", label: "Stolen", hint: "Taken from the rider or the vehicle." },
  { code: "DESTROYED", label: "Destroyed", hint: "Damaged beyond delivery or use." },
  { code: "UNRECOVERABLE", label: "Otherwise unrecoverable", hint: "Anything else that ends this parcel's journey." },
];

const MIN_JUSTIFICATION = 10;
const MAX_TEXT = 1000;

export function assetLossErrorMessage(error) {
  const code = error?.response?.data?.code || error?.response?.data?.msg;
  const status = error?.response?.status;
  if (code === "ASSET_LOSS_SELF_AUTHORIZED") {
    return "Someone other than the rider holding this parcel has to authorise a write-off.";
  }
  if (code === "ASSET_LOSS_NOT_IN_CUSTODY") {
    return "This parcel is no longer in a rider's custody, so there is nothing to write off. Refresh to see its current state.";
  }
  if (code === "ASSET_LOSS_RECEIPT_REFUSED" || code === "ASSET_LOSS_RIDER_AUTHORITY_REFUSED") {
    return "A write-off records that nobody received this parcel. It cannot name a receiver or a rider.";
  }
  if (status === 409) {
    return "Someone acted on this parcel first. Reload and check its current state before writing it off.";
  }
  if (status === 403) {
    return "You do not have permission to write off a parcel.";
  }
  return "The parcel could not be written off. Please try again.";
}

export default function AssetLossPanel({ parcel, businessId, onRecorded, onCancel }) {
  const [reasonCode, setReasonCode] = useState("");
  const [justification, setJustification] = useState("");
  const [riderStatement, setRiderStatement] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // One key per opened panel, not per click: a double submit replays rather
  // than writing a second disposition.
  const idempotencyKey = useMemo(() => createClientRequestId("asset_loss"), []);

  const trimmed = justification.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_JUSTIFICATION;
  const ready = Boolean(reasonCode) && trimmed.length >= MIN_JUSTIFICATION && acknowledged && !busy;

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setError("");
    try {
      const result = await businessAssetLossService.recordLoss(businessId, parcel, {
        reasonCode,
        justification: trimmed,
        riderStatement: riderStatement.trim() || undefined,
        idempotencyKey,
      });
      onRecorded?.(result);
    } catch (requestError) {
      // Already written off is the outcome the operator wanted, not a failure.
      if (requestError?.response?.data?.code === "ASSET_LOSS_ALREADY_DISPOSED") {
        onRecorded?.({ alreadyRecorded: true });
        return;
      }
      // Everything typed is preserved: nobody should have to rewrite a
      // justification because somebody else touched the parcel first.
      setError(assetLossErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="asset-loss-panel"
      className="mt-4 rounded-2xl border border-danger bg-danger-subtle p-4 text-danger"
    >
      <h3 className="flex items-center gap-2 font-semibold">
        <PackageX className="h-5 w-5" aria-hidden="true" /> This parcel cannot be recovered
      </h3>
      <p className="mt-2 text-sm">
        Records that the parcel is gone and closes the rider&apos;s responsibility for holding it. It does
        not refund or write off any money, and it does not close an investigation.
      </p>

      <fieldset className="mt-4">
        <legend className="text-sm font-semibold">What happened to it?</legend>
        <div className="mt-2 grid gap-2">
          {REASONS.map((reason) => (
            <label
              key={reason.code}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-sm ${reasonCode === reason.code
                ? "border-danger bg-white dark:bg-neutral-900"
                : "border-danger"}`}
            >
              <input
                type="radio"
                name={`asset-loss-reason-${parcel.shipmentId}`}
                value={reason.code}
                checked={reasonCode === reason.code}
                onChange={() => setReasonCode(reason.code)}
                className="mt-0.5 h-4 w-4 shrink-0"
              />
              <span>
                <strong>{reason.label}</strong>
                <span className="block text-danger/80">{reason.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="mt-4 block text-sm font-semibold">
        What happened, in your words
        <textarea
          value={justification}
          onChange={(event) => setJustification(event.target.value)}
          maxLength={MAX_TEXT}
          rows={3}
          aria-invalid={tooShort ? "true" : undefined}
          aria-describedby={`asset-loss-justification-help-${parcel.shipmentId}`}
          className="mt-2 w-full rounded-xl border border-danger bg-white p-3 text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:bg-neutral-900 dark:text-white"
          placeholder="What was established, and how?"
        />
      </label>
      <p
        id={`asset-loss-justification-help-${parcel.shipmentId}`}
        className={`mt-1 text-xs ${tooShort ? "font-medium text-danger" : "text-danger/70"}`}
      >
        {tooShort
          ? `At least ${MIN_JUSTIFICATION} characters — this is the record an investigation reads.`
          : `Required. ${trimmed.length}/${MAX_TEXT} characters.`}
      </p>

      <label className="mt-4 block text-sm font-semibold">
        What the rider said <span className="font-normal">(optional)</span>
        <textarea
          value={riderStatement}
          onChange={(event) => setRiderStatement(event.target.value)}
          maxLength={MAX_TEXT}
          rows={2}
          className="mt-2 w-full rounded-xl border border-danger bg-white p-3 text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:bg-neutral-900 dark:text-white"
          placeholder="Recorded as evidence only."
        />
      </label>
      <p className="mt-1 text-xs text-danger/70">
        Kept as evidence. A rider&apos;s own account cannot authorise this on its own.
      </p>

      <label className="mt-4 flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0"
        />
        <span>
          I understand this closes the rider&apos;s custody of the parcel, and does not settle money owed
          or end any investigation.
        </span>
      </label>

      {error && (
        <p role="alert" className="mt-4 flex items-start gap-2 rounded-xl bg-white p-3 text-sm font-medium text-danger dark:bg-neutral-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={!ready}
          className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-danger px-4 font-semibold text-on-accent hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <PackageX className="h-5 w-5" aria-hidden="true" />}
          {busy ? "Recording…" : "Write off this parcel"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="min-h-12 rounded-xl border border-danger px-4 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
